import { useEffect, useRef } from "react"

import { clarity } from "clarity-js"
import { useRecoilValue } from "recoil"

import { userStateForAnalytics } from "@fuse/recoil/signup"

// Declare clarity as a global variable
declare global {
  interface Window {
    clarity: {
      getState: () => { initialized: boolean } | undefined
      consent: () => void
      start: (config: any) => void
    }
  }
}

const useClarityMonitor = (): void => {
  const { userId, isAnonymous } = useRecoilValue(userStateForAnalytics)
  const previousError = useRef<string | null>(null)
  const lastStateChange = useRef<number>(Date.now())

  useEffect(() => {
    const getBrowserInfo = () => {
      try {
        return {
          userAgent: navigator.userAgent,
          language: navigator.language,
          doNotTrack: navigator.doNotTrack,
          cookieEnabled: navigator.cookieEnabled,
          localStorage: !!window.localStorage,
          sessionStorage: !!window.sessionStorage,
        }
      } catch (e) {
        return { error: "Failed to get browser info" }
      }
    }

    const getScriptInfo = () => {
      try {
        // Get all script tags
        const allScripts = Array.from(document.scripts) as HTMLScriptElement[]
        // Get all script sources for debugging
        const allScriptSources = allScripts.map((script) => ({
          src: script.src,
          async: script.async,
          defer: script.defer,
          type: script.type,
        }))
        // Check for dynamically loaded scripts
        const dynamicScripts = performance
          .getEntriesByType("resource")
          .filter((entry) => entry.name.includes("clarity"))
          .map((entry) => ({
            url: entry.name,
            duration: entry.duration,
            timestamp: entry.startTime,
          }))
        return {
          allScriptSources,
          dynamicScripts,
          totalScripts: allScripts.length,
        }
      } catch (e) {
        return {
          error: "Failed to get script info",
          errorMessage: e instanceof Error ? e.message : "Unknown error",
        }
      }
    }

    const getClarityState = () => {
      try {
        const getStateExists = typeof (clarity as any)?.getState === "function"
        const state = getStateExists ? (clarity as any).getState() : undefined
        return {
          getStateExists,
          state,
          isInitialized: state?.initialized === true,
          consentCalled: typeof clarity?.consent === "function",
          startCalled: typeof clarity?.start === "function",
        }
      } catch (e) {
        return { error: "Failed to get Clarity state" }
      }
    }

    const getPageContext = () => {
      try {
        return {
          page: window.location.pathname,
          isIncognito: !window.localStorage ? true : false,
          userId,
          isAnonymous,
          timestamp: new Date().toISOString(),
          timeOnPage: Date.now() - performance.timing.navigationStart,
          hasInteracted: document.hasFocus(),
          activeElement: document.activeElement?.tagName,
        }
      } catch (e) {
        return { error: "Failed to get page context" }
      }
    }

    const checkClarity = () => {
      try {
        // Collect all diagnostic information
        const diagnostics = {
          clarityState: getClarityState(),
          browserInfo: getBrowserInfo(),
          scriptInfo: getScriptInfo(),
          pageContext: getPageContext(),
        }
        const isInitialized = diagnostics.clarityState.isInitialized
        if (!isInitialized) {
          const currentError = `Clarity not initialized on ${window.location.pathname}`
          if (currentError !== previousError.current) {
            console.error("Clarity initialization issue", diagnostics)
            previousError.current = currentError
            lastStateChange.current = Date.now()
          }
        } else if (previousError.current) {
          console.log("Clarity recovered", diagnostics)
          previousError.current = null
          lastStateChange.current = Date.now()
        }
      } catch (e) {
        const currentError = `Clarity monitoring error: ${e instanceof Error ? e.message : "Unknown error"}`
        if (currentError !== previousError.current) {
          console.error("Clarity monitoring error:", {
            error: e,
            basicInfo: {
              page: window.location.pathname,
              timestamp: new Date().toISOString(),
            },
          })
          previousError.current = currentError
        }
      }
    }

    // Check once on mount
    checkClarity()
    // Check again after 30 seconds
    const timeoutId = setTimeout(checkClarity, 30000)
    return () => clearTimeout(timeoutId)
  }, [userId, isAnonymous])
}

export default useClarityMonitor
